import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, ExternalLinkIcon, LocationMarkerIcon, OfficeBuildingIcon } from '@heroicons/react/outline';
import { formatTitle } from '../utils/formatTitle';
import { nativeApply } from '../utils/applyConfig';
import { feature } from '../utils/features';
import { isNew, isOpen, jobType, prettyDate } from '../utils/dates';
import SaveButton from './SaveButton';

// The refreshed job card (feature "modernCards", RefugeeJobLeads first). The whole card is one link
// (the title's link is stretched over it); the save button sits above it.

// "16.50" -> "$16.50"; anything already worded is left alone.
const pay = (salary) => {
  const s = String(salary || '').trim();
  return /^\d/.test(s) ? `$${s}` : s;
};

export default function ModernCard({ btnId, title, companyName, categories, city, salary, postedOn, externalUrl, source, postExpiry }) {
  const open = isOpen(postExpiry);
  const direct = !externalUrl && open && Boolean(nativeApply());
  const name = formatTitle(title);
  const wage = pay(salary);
  const type = jobType(categories, Boolean(externalUrl));
  const linkClass = 'after:absolute after:inset-0 after:rounded-2xl focus:outline-none';
  return (
    <article
      className={`group relative mb-4 flex flex-col rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-200 transition duration-200 focus-within:ring-2 focus-within:ring-primary hover:shadow-lg hover:ring-primary/60 sm:p-6 ${
        open ? '' : 'bg-gray-50'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {(direct || !open || (open && isNew(postedOn))) && (
            <div className="mb-2 flex flex-wrap gap-1.5">
              {open && isNew(postedOn) && <span className="rounded-md bg-green-50 px-2 py-0.5 text-xs font-semibold text-green-700 ring-1 ring-green-200">New</span>}
              {direct && <span className="rounded-md bg-primary-light px-2 py-0.5 text-xs font-semibold text-primary-dark ring-1 ring-primary/30">Direct apply</span>}
              {!open && <span className="rounded-md bg-gray-200 px-2 py-0.5 text-xs font-semibold text-gray-600">Closed</span>}
            </div>
          )}
          <h3 className={`font-display text-lg font-bold leading-snug sm:text-xl ${open ? 'text-gray-900 group-hover:text-primary-dark' : 'text-gray-500'}`}>
            {externalUrl ? (
              <a href={externalUrl} target="_blank" rel="noopener noreferrer nofollow" className={linkClass}>
                {name}
                <span className="sr-only"> (opens the employer's site)</span>
              </a>
            ) : (
              <Link to={`/jobs/${btnId}`} className={linkClass}>
                {name}
              </Link>
            )}
          </h3>
          <p className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-600">
            {companyName && (
              <span className="inline-flex items-center gap-1">
                <OfficeBuildingIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
                {companyName}
              </span>
            )}
            {city && (
              <span className="inline-flex items-center gap-1">
                <LocationMarkerIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
                {city}
              </span>
            )}
          </p>
        </div>
        {feature('savedJobs') && (
          <div className="relative z-10 -mr-2 -mt-2">
            <SaveButton job={{ id: btnId, title: name, companyName, categories, city, salary, postedOn, postExpiry, externalUrl, source }} />
          </div>
        )}
      </div>

      {(wage || type) && (
        <div className="mt-4 flex flex-wrap gap-2">
          {wage && <span className="rounded-lg bg-green-50 px-2.5 py-1 text-sm font-semibold text-green-800 ring-1 ring-green-100">{wage}</span>}
          {type && <span className="rounded-lg bg-gray-900/5 px-2.5 py-1 text-sm text-gray-700">{type}</span>}
        </div>
      )}

      <div className="mt-5 flex items-center justify-between gap-3 border-t border-gray-100 pt-4 text-sm">
        <span className="text-gray-500">{source ? `via ${source}` : postedOn ? `Posted ${prettyDate(postedOn)}` : ''}</span>
        <span className="pointer-events-none inline-flex items-center gap-1 font-semibold text-primary-dark">
          {externalUrl ? 'Employer site' : open ? 'View job' : 'View'}
          {externalUrl ? (
            <ExternalLinkIcon className="h-4 w-4" aria-hidden="true" />
          ) : (
            <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
          )}
        </span>
      </div>
    </article>
  );
}
